<template>
  <div class="batch-add-availability">
    <div class="batch-add-container">
      <!-- 页面标题 -->
      <div class="page-header">
        <h2>{{ $t('batchAddAvailability.title') }}</h2>
        <p class="page-description">{{ $t('batchAddAvailability.description') }}</p>
      </div>

      <!-- 表单区域 -->
      <el-form :model="form" :rules="formRules" ref="batchForm" label-width="120px" class="batch-form">
        <!-- 选择教练 -->
        <el-form-item :label="$t('batchAddAvailability.selectCoaches')" prop="selectedCoaches" required>
          <div class="coach-selection">
            <el-checkbox 
              v-model="selectAllCoaches" 
              @change="handleSelectAll"
              class="select-all-checkbox">
              {{ $t('batchAddAvailability.selectAll') }}
            </el-checkbox>
            <div class="coach-list">
              <el-checkbox-group v-model="form.selectedCoaches" @change="handleCoachSelection">
                <el-row :gutter="10">
                  <el-col :span="8" v-for="coach in coachesList" :key="coach.id">
                    <el-checkbox 
                      :label="coach.id" 
                      class="coach-checkbox">
                      <div class="coach-info">
                        <img :src="coach.avatar || defaultAvatar" alt="头像" class="coach-avatar-small">
                        <div class="coach-details">
                          <div class="coach-name">{{ coach.name }}</div>
                          <div class="coach-id">{{ coach.id }}</div>
                        </div>
                      </div>
                    </el-checkbox>
                  </el-col>
                </el-row>
              </el-checkbox-group>
            </div>
          </div>
        </el-form-item>

        <!-- 选择日期 -->
        <el-form-item :label="$t('batchAddAvailability.selectDates')" prop="selectedDates" required>
          <div class="date-selection">
            <!-- 单个日期选择 -->
            <div class="date-mode">
              <el-radio-group v-model="dateMode" @change="handleDateModeChange">
                <el-radio label="single">{{ $t('batchAddAvailability.singleDate') }}</el-radio>
                <el-radio label="multiple">{{ $t('batchAddAvailability.multipleDates') }}</el-radio>
                <el-radio label="range">{{ $t('batchAddAvailability.dateRange') }}</el-radio>
              </el-radio-group>
            </div>
            
            <!-- 单个日期 -->
            <div v-if="dateMode === 'single'" class="single-date">
              <el-date-picker
                v-model="singleDate"
                type="date"
                :placeholder="$t('batchAddAvailability.selectDate')"
                :picker-options="datePickerOptions"
                @change="handleSingleDateChange">
              </el-date-picker>
            </div>

            <!-- 多个日期 -->
            <div v-if="dateMode === 'multiple'" class="multiple-dates">
              <el-date-picker
                v-model="multipleDates"
                type="dates"
                :placeholder="$t('batchAddAvailability.selectMultipleDates')"
                :picker-options="datePickerOptions"
                @change="handleMultipleDatesChange">
              </el-date-picker>
            </div>

            <!-- 日期范围 -->
            <div v-if="dateMode === 'range'" class="date-range">
              <el-date-picker
                v-model="dateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                :picker-options="datePickerOptions"
                @change="handleDateRangeChange">
              </el-date-picker>
            </div>

            <!-- 显示已选择的日期 -->
            <div v-if="form.selectedDates.length > 0" class="selected-dates">
              <h4>{{ $t('batchAddAvailability.selectedDates') }}：</h4>
              <el-tag 
                v-for="date in form.selectedDates" 
                :key="date"
                closable
                @close="removeSelectedDate(date)"
                class="date-tag">
                {{ formatDisplayDate(date) }}
              </el-tag>
            </div>
          </div>
        </el-form-item>

        <!-- 时间设置 -->
        <el-form-item :label="$t('batchAddAvailability.selectTimeSlots')" required>
          <div class="time-settings">
            <!-- 时间段模式选择 -->
            <div class="time-mode">
              <el-radio-group v-model="timeMode" @change="handleTimeModeChange">
                <el-radio label="preset">{{ $t('batchAddAvailability.presetTimeSlots') }}</el-radio>
                <el-radio label="custom">{{ $t('batchAddAvailability.customTimeSlots') }}</el-radio>
              </el-radio-group>
            </div>

            <!-- 预设时间段 -->
            <div v-if="timeMode === 'preset'" class="preset-times">
              <el-checkbox-group v-model="form.presetTimeSlots">
                <el-row :gutter="10">
                  <el-col :span="6" v-for="preset in presetTimeSlots" :key="preset.key">
                    <el-checkbox 
                      :label="preset.key"
                      class="time-preset-checkbox">
                      {{ preset.label }}
                    </el-checkbox>
                  </el-col>
                </el-row>
              </el-checkbox-group>
            </div>

            <!-- 自定义时间段 -->
            <div v-if="timeMode === 'custom'" class="custom-times">
              <div class="time-slots-container">
                <div 
                  v-for="(slot, index) in form.customTimeSlots" 
                  :key="index"
                  class="time-slot-item">
                  <el-time-picker
                    v-model="slot.startTime"
                    :placeholder="$t('batchAddAvailability.startTime')"
                    format="HH:mm"
                    value-format="HH:mm">
                  </el-time-picker>
                  <span class="time-separator">-</span>
                  <el-time-picker
                    v-model="slot.endTime"
                    :placeholder="$t('batchAddAvailability.endTime')"
                    format="HH:mm"
                    value-format="HH:mm">
                  </el-time-picker>
                  <el-button 
                    type="danger" 
                    icon="el-icon-delete" 
                    size="mini"
                    @click="removeTimeSlot(index)"
                    :disabled="form.customTimeSlots.length <= 1">
                  </el-button>
                </div>
                <el-button 
                  type="text" 
                  icon="el-icon-plus"
                  @click="addTimeSlot"
                  class="add-time-slot-btn">
                  {{ $t('batchAddAvailability.addTimeSlot') }}
                </el-button>
              </div>
            </div>
          </div>
        </el-form-item>

        <!-- 备注 -->
        <el-form-item :label="$t('batchAddAvailability.remark')">
          <el-input
            v-model="form.remark"
            type="textarea"
            :placeholder="$t('common.optionalRemark')"
            :rows="3"
            maxlength="200"
            show-word-limit>
          </el-input>
        </el-form-item>

        <!-- 操作按钮 -->
        <el-form-item>
          <div class="form-actions">
            <el-button 
              type="primary" 
              @click="previewBatch"
              :loading="previewing"
              size="medium">
              {{ $t('common.preview') }}
            </el-button>
            <el-button 
              @click="resetForm"
              size="medium">
              {{ $t('common.reset') }}
            </el-button>
            <el-button 
              @click="goBack"
              size="medium">
              {{ $t('common.back') }}
            </el-button>
          </div>
        </el-form-item>
      </el-form>
    </div>

    <!-- 预览对话框 -->
    <el-dialog
      title="批量添加预览"
      :visible.sync="showPreviewDialog"
      width="70%"
      :close-on-click-modal="false">
      <div class="preview-content">
        <div class="preview-summary">
          <h3>批量操作摘要</h3>
          <p>将为 <strong>{{ form.selectedCoaches.length }}</strong> 个教练，在 <strong>{{ form.selectedDates.length }}</strong> 个日期，添加 <strong>{{ getTimeSlotCount() }}</strong> 个时间段</p>
          <p>总计将创建 <strong>{{ getTotalSlotCount() }}</strong> 条空闲时间记录</p>
        </div>

        <div class="preview-details">
          <h4>详细信息：</h4>
          <el-table :data="previewData" border style="width: 100%" max-height="400">
            <el-table-column prop="coachName" label="教练" width="120"></el-table-column>
            <el-table-column prop="coachId" label="教练编号" width="120"></el-table-column>
            <el-table-column prop="date" label="日期" width="120"></el-table-column>
            <el-table-column prop="timeSlot" label="时间段" width="150"></el-table-column>
            <el-table-column prop="startDateTime" label="开始时间" width="160"></el-table-column>
            <el-table-column prop="endDateTime" label="结束时间" width="160"></el-table-column>
          </el-table>
        </div>

        <div class="preview-actions">
          <el-button 
            type="primary" 
            @click="confirmBatchAdd"
            :loading="submitting"
            size="medium">
            确认批量添加
          </el-button>
          <el-button 
            @click="showPreviewDialog = false"
            size="medium">
            取消
          </el-button>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'BatchAddAvailability',
  data() {
    return {
      // 表单数据
      form: {
        selectedCoaches: [], // 选中的教练ID列表
        selectedDates: [], // 选中的日期列表
        presetTimeSlots: [], // 选中的预设时间段
        customTimeSlots: [{ startTime: '', endTime: '' }], // 自定义时间段
        remark: ''
      },
      
      // 表单验证规则
      formRules: {
        selectedCoaches: [
          { required: true, message: '请至少选择一个教练', trigger: 'change' }
        ],
        selectedDates: [
          { required: true, message: '请至少选择一个日期', trigger: 'change' }
        ]
      },

      // 教练相关
      coachesList: [],
      selectAllCoaches: false,
      defaultAvatar: require('@/assets/img/default-avatar.png'),

      // 日期相关
      dateMode: 'single', // single, multiple, range
      singleDate: null,
      multipleDates: [],
      dateRange: [],
      datePickerOptions: {
        disabledDate: (time) => {
          // 只禁用过去的日期，允许选择今天及以后的任意日期
          return time.getTime() < Date.now() - 8.64e7;
        }
      },

      // 时间相关
      timeMode: 'preset', // preset, custom
      presetTimeSlots: [
        { key: 'morning', label: this.$t('batchAddAvailability.morningSlot'), start: '08:00', end: '12:00' },
        { key: 'afternoon', label: this.$t('batchAddAvailability.afternoonSlot'), start: '13:00', end: '17:00' },
        { key: 'evening', label: this.$t('batchAddAvailability.eveningSlot'), start: '18:00', end: '22:00' },
        { key: 'fullday', label: this.$t('batchAddAvailability.fullDaySlot'), start: '08:00', end: '22:00' }
      ],

      // 对话框状态
      showPreviewDialog: false,
      previewData: [],
      
      // 加载状态
      loading: false,
      previewing: false,
      submitting: false
    };
  },

  created() {
    this.fetchCoaches();
  },

  methods: {
    // 获取教练列表
    fetchCoaches() {
      this.loading = true;
      this.$http.get('/jiaolian/page', {
        params: {
          page: 1,
          limit: 1000, // 获取所有教练
          sort: 'id'
        }
      })
      .then(response => {
        if (response.data && response.data.code === 0) {
          const coaches = response.data.data?.list || [];
          this.coachesList = coaches.map(coach => ({
            id: coach.jiaolianbianhao || '未知编号',
            name: coach.jiaolianxingming || '未知姓名',
            avatar: coach.touxiang ? this.$base.imgurl + coach.touxiang.split(',')[0] : this.defaultAvatar,
            level: coach.jiaoliandengji || '无等级',
            originalData: coach
          }));
        } else {
          this.$message.error('获取教练列表失败：' + (response.data?.msg || '未知错误'));
        }
      })
      .catch(error => {
        console.error('获取教练列表错误:', error);
        this.$message.error('获取教练列表出错：' + (error.message || '网络错误'));
      })
      .finally(() => {
        this.loading = false;
      });
    },

    // 处理全选教练
    handleSelectAll(val) {
      if (val) {
        this.form.selectedCoaches = this.coachesList.map(coach => coach.id);
      } else {
        this.form.selectedCoaches = [];
      }
    },

    // 处理教练选择
    handleCoachSelection(selection) {
      this.selectAllCoaches = selection.length === this.coachesList.length;
    },

    // 处理日期模式变化
    handleDateModeChange(mode) {
      this.form.selectedDates = [];
      this.singleDate = null;
      this.multipleDates = [];
      this.dateRange = [];
    },

    // 处理单个日期选择
    handleSingleDateChange(date) {
      if (date) {
        this.form.selectedDates = [this.formatDate(date)];
      } else {
        this.form.selectedDates = [];
      }
    },

    // 处理多个日期选择
    handleMultipleDatesChange(dates) {
      if (dates && dates.length > 0) {
        this.form.selectedDates = dates.map(date => this.formatDate(date));
      } else {
        this.form.selectedDates = [];
      }
    },

    // 处理日期范围选择
    handleDateRangeChange(range) {
      if (range && range.length === 2) {
        const [start, end] = range;
        const dates = [];
        const currentDate = new Date(start);
        const endDate = new Date(end);
        
        while (currentDate <= endDate) {
          dates.push(this.formatDate(new Date(currentDate)));
          currentDate.setDate(currentDate.getDate() + 1);
        }
        
        this.form.selectedDates = dates;
      } else {
        this.form.selectedDates = [];
      }
    },

    // 移除选中的日期
    removeSelectedDate(date) {
      const index = this.form.selectedDates.indexOf(date);
      if (index > -1) {
        this.form.selectedDates.splice(index, 1);
      }
    },

    // 处理时间模式变化
    handleTimeModeChange(mode) {
      if (mode === 'preset') {
        this.form.presetTimeSlots = [];
      } else {
        this.form.customTimeSlots = [{ startTime: '', endTime: '' }];
      }
    },

    // 添加时间段
    addTimeSlot() {
      this.form.customTimeSlots.push({ startTime: '', endTime: '' });
    },

    // 移除时间段
    removeTimeSlot(index) {
      if (this.form.customTimeSlots.length > 1) {
        this.form.customTimeSlots.splice(index, 1);
      }
    },

    // 获取时间段数量
    getTimeSlotCount() {
      if (this.timeMode === 'preset') {
        return this.form.presetTimeSlots.length;
      } else {
        return this.form.customTimeSlots.filter(slot => slot.startTime && slot.endTime).length;
      }
    },

    // 获取总记录数
    getTotalSlotCount() {
      return this.form.selectedCoaches.length * this.form.selectedDates.length * this.getTimeSlotCount();
    },

    // 预览批量添加
    previewBatch() {
      this.$refs.batchForm.validate(valid => {
        // 检查基本表单验证
        if (!valid) return;

        // 检查教练选择
        if (this.form.selectedCoaches.length === 0) {
          this.$message.error('请至少选择一个教练');
          return;
        }

        // 检查日期选择
        if (this.form.selectedDates.length === 0) {
          this.$message.error('请至少选择一个日期');
          return;
        }

        // 检查时间段设置
        if (this.timeMode === 'preset') {
          if (this.form.presetTimeSlots.length === 0) {
            this.$message.error(this.$t('batchAddAvailability.selectAtLeastOnePreset'));
            return;
          }
        } else {
          // 检查自定义时间段
          const validCustomSlots = this.form.customTimeSlots.filter(slot => slot.startTime && slot.endTime);
          if (validCustomSlots.length === 0) {
            this.$message.error(this.$t('batchAddAvailability.selectAtLeastOneCustom'));
            return;
          }
          
          // 检查时间段的合理性
          for (let slot of validCustomSlots) {
            if (slot.startTime >= slot.endTime) {
              this.$message.error('开始时间必须早于结束时间');
              return;
            }
          }
        }

        this.generatePreviewData();
        this.showPreviewDialog = true;
      });
    },

    // 生成预览数据
    generatePreviewData() {
      this.previewData = [];
      
      // 获取时间段列表
      const timeSlots = this.getTimeSlotsList();
      
      // 生成预览数据
      this.form.selectedCoaches.forEach(coachId => {
        const coach = this.coachesList.find(c => c.id === coachId);
        
        this.form.selectedDates.forEach(date => {
          timeSlots.forEach(slot => {
            this.previewData.push({
              coachId: coachId,
              coachName: coach ? coach.name : '未知教练',
              date: date,
              timeSlot: `${slot.start}-${slot.end}`,
              startDateTime: `${date} ${slot.start}:00`,
              endDateTime: `${date} ${slot.end}:00`
            });
          });
        });
      });
    },

    // 获取时间段列表
    getTimeSlotsList() {
      const slots = [];
      
      if (this.timeMode === 'preset') {
        this.form.presetTimeSlots.forEach(key => {
          const preset = this.presetTimeSlots.find(p => p.key === key);
          if (preset) {
            slots.push({ start: preset.start, end: preset.end });
          }
        });
      } else {
        this.form.customTimeSlots.forEach(slot => {
          if (slot.startTime && slot.endTime) {
            slots.push({ start: slot.startTime, end: slot.endTime });
          }
        });
      }
      
      return slots;
    },

    // 确认批量添加
    confirmBatchAdd() {
      this.submitting = true;
      
      const requests = [];
      const timeSlots = this.getTimeSlotsList();
      
      // 生成所有请求
      this.form.selectedCoaches.forEach(coachId => {
        this.form.selectedDates.forEach(date => {
          timeSlots.forEach(slot => {
            const startDateTime = `${date} ${slot.start}:00`;
            const endDateTime = `${date} ${slot.end}:00`;
            
            const queryString = `?jiaolianbianhao=${encodeURIComponent(coachId)}&availableFrom=${encodeURIComponent(startDateTime)}&availableUntil=${encodeURIComponent(endDateTime)}`;
            
            requests.push(
              this.$http.post(`/schedule/setAvailability${queryString}`)
            );
          });
        });
      });

      // 批量执行请求
      Promise.allSettled(requests)
        .then(results => {
          const successful = results.filter(result => result.status === 'fulfilled' && result.value.data?.code === 0).length;
          const failed = results.length - successful;
          
          if (failed === 0) {
            this.$message.success(`批量添加成功！共添加了 ${successful} 条空闲时间记录`);
            this.resetForm();
            this.showPreviewDialog = false;
            this.goBack();
          } else {
            this.$message.warning(`批量添加完成！成功 ${successful} 条，失败 ${failed} 条`);
          }
        })
        .catch(error => {
          console.error('批量添加错误:', error);
          this.$message.error('批量添加失败');
        })
        .finally(() => {
          this.submitting = false;
        });
    },

    // 重置表单
    resetForm() {
      this.form = {
        selectedCoaches: [],
        selectedDates: [],
        presetTimeSlots: [],
        customTimeSlots: [{ startTime: '', endTime: '' }],
        remark: ''
      };
      this.selectAllCoaches = false;
      this.singleDate = null;
      this.multipleDates = [];
      this.dateRange = [];
      this.dateMode = 'single';
      this.timeMode = 'preset';
      this.$refs.batchForm && this.$refs.batchForm.clearValidate();
    },

    // 返回上一页
    goBack() {
      this.$router.go(-1);
    },

    // 格式化日期
    formatDate(date) {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    },

    // 格式化显示日期
    formatDisplayDate(dateStr) {
      const date = new Date(dateStr);
      const days = ['日', '一', '二', '三', '四', '五', '六'];
      const dayOfWeek = days[date.getDay()];
      return `${dateStr} (周${dayOfWeek})`;
    }
  }
};
</script>

<style scoped>
.batch-add-availability {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;
}

.batch-add-container {
  max-width: 1200px;
  margin: 0 auto;
  background: white;
  padding: 30px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.page-header {
  margin-bottom: 30px;
  text-align: center;
}

.page-header h2 {
  color: #333;
  margin-bottom: 10px;
}

.page-description {
  color: #666;
  font-size: 14px;
}

.batch-form {
  margin-top: 30px;
}

/* 教练选择样式 */
.coach-selection {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
}

.select-all-checkbox {
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
}

.coach-list {
  max-height: 300px;
  overflow-y: auto;
}

.coach-checkbox {
  margin-bottom: 10px;
  width: 100%;
}

.coach-info {
  display: flex;
  align-items: center;
  padding: 8px;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  background: #fafafa;
}

.coach-avatar-small {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  margin-right: 10px;
}

.coach-details {
  flex: 1;
}

.coach-name {
  font-weight: bold;
  color: #333;
}

.coach-id {
  font-size: 12px;
  color: #666;
}

/* 日期选择样式 */
.date-selection {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
}

.date-mode {
  margin-bottom: 15px;
}

.single-date, .multiple-dates, .date-range {
  margin-bottom: 15px;
}

.selected-dates {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #e4e7ed;
}

.selected-dates h4 {
  margin-bottom: 10px;
  color: #333;
}

.date-tag {
  margin: 5px 5px 5px 0;
}

/* 时间设置样式 */
.time-settings {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 15px;
}

.time-mode {
  margin-bottom: 15px;
}

.preset-times {
  margin-top: 15px;
}

.time-preset-checkbox {
  margin-bottom: 10px;
  width: 100%;
}

.custom-times {
  margin-top: 15px;
}

.time-slot-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.time-separator {
  margin: 0 10px;
  color: #666;
}

.add-time-slot-btn {
  margin-top: 10px;
}

/* 表单操作 */
.form-actions {
  text-align: center;
  margin-top: 30px;
}

.form-actions .el-button {
  margin: 0 10px;
}

/* 预览对话框样式 */
.preview-content {
  padding: 20px;
}

.preview-summary {
  margin-bottom: 20px;
  padding: 15px;
  background: #f0f9ff;
  border-radius: 4px;
  border-left: 4px solid #409eff;
}

.preview-summary h3 {
  margin-bottom: 10px;
  color: #333;
}

.preview-summary p {
  margin: 5px 0;
  color: #666;
}

.preview-details {
  margin-bottom: 20px;
}

.preview-details h4 {
  margin-bottom: 15px;
  color: #333;
}

.preview-actions {
  text-align: center;
  margin-top: 20px;
}

.preview-actions .el-button {
  margin: 0 10px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .batch-add-container {
    padding: 20px;
  }
  
  .coach-info {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .coach-avatar-small {
    margin-bottom: 5px;
  }
}
</style> 